import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  Icon,
  HStack,
  Image,
  useColorModeValue,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  List,
  ListItem,
  ListIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import { useState } from "react";
import type { IconType } from "react-icons";
import {
  FiShield,
  FiActivity,
  FiSettings,
  FiUsers,
  FiCheck,
} from "react-icons/fi";

interface CommitteeMember {
  name: string;
  role: string;
}

interface CommitteeMemberWithAvatar extends CommitteeMember {
  avatar?: string;
}

const committeeMemberAvatars: Record<string, string> = {
  "سید محمدجواد فرهانیان": "/images/team-photos/فرهانیان.png",
  "نوید رفیعی تبریزی": "/images/team-photos/نوید رفیعی.png",
  "دکتر نوید رفیعی": "/images/team-photos/نوید رفیعی.png",
  "محمد ابراهیم راعی عزآبادی": "/images/team-photos/راعی.png",
  "محمد راعی": "/images/team-photos/راعی.png",
  "محمد خزائی کوهپر": "/images/team-photos/محمد خزائی کوهپر.png",
  "مهرداد فصیحی": "/images/team-photos/فصیحی.png",
  "کریم بروفرد": "/images/team-photos/بروفرد.png",
  "حمید ساروقیان": "/images/team-photos/حمید ساروقیان.png",
  "مرتضی اثباتی": "/images/team-photos/مرتضی اثباتی.png",
  "محمد سجاد سیاهکارزاده": "/images/team-photos/سیاهکارزاده.png",
  "محمدرضا مایلی": "/images/team-photos/محمدرضا مایلی.png",
  "محمد هاشمی‌نژاد": "/images/team-photos/هاشمی.webp",
  "محمدرضا صادقی مقدم": "/images/team-photos/صادقی مقدم.png",
  "محمدحسین عسگری": "/images/team-photos/عسگری.png",
  "عبدالله دریابر": "/images/team-photos/دریابر.png",
  "محمد صفری": "/images/team-photos/صفری.png",
  "پیمان تاتائی": "/images/team-photos/تاتایی.png",
  "محمد هادی بحرالعلوم": "/images/team-photos/بحرالعلوم.png",
};

interface Committee {
  icon: IconType;
  name: string;
  intro: string;
  duties: string[];
  members: CommitteeMember[];
}

const committees: Committee[] = [
  {
    icon: FiShield,
    name: "کمیته حسابرسی",
    intro:
      "کمیته حسابرسی با هدف تقویت نظام نظارتی، ارتقای شفافیت مالی و اطمینان از صحت فرآیندهای گزارشگری مالی شرکت تشکیل شده است.",
    duties: [
      "نظارت بر فرآیند گزارشگری مالی و اطمینان از صحت و شفافیت صورت‌های مالی",
      "بررسی عملکرد حسابرسی داخلی و تعامل مؤثر با حسابرس مستقل",
      "ارزیابی کفایت و اثربخشی سیستم‌های کنترل داخلی",
      "نظارت بر رعایت الزامات قانونی و مقررات حاکم",
      "ارائه گزارش‌های منظم به هیئت‌مدیره در خصوص ریسک‌های مالی و انطباق",
    ],
    members: [
      { name: "سید محمدجواد فرهانیان", role: "رئیس کمیته" },
      { name: "محمدعلی بیداری", role: "عضو کمیته" },
      { name: "علی کریمی", role: "عضو کمیته" },
      { name: "مهرداد فصیحی", role: "عضو کمیته" },
      { name: "مهدی دهقانی اشکذری", role: "عضو کمیته" },
    ],
  },
  {
    icon: FiActivity,
    name: "کمیته ریسک",
    intro:
      "کمیته ریسک با هدف شناسایی، مدیریت و کنترل ریسک‌های مؤثر بر فعالیت‌های شرکت و حفظ تعادل میان ریسک و بازده تشکیل شده است.",
    duties: [
      "شناسایی، اندازه‌گیری و پایش انواع ریسک‌های شرکت از جمله ریسک‌های بازار، اعتباری، نقدینگی و عملیاتی",
      "بررسی و به‌روزرسانی چارچوب‌ها و سیاست‌های مدیریت ریسک",
      "نظارت بر رعایت حدود و سقف‌های ریسک‌پذیری مصوب",
      "ارائه پیشنهادهای اصلاحی به هیئت‌مدیره در صورت انحراف از حدود تعیین‌شده",
      "ارزیابی ریسک‌های مرتبط با ابزارها، محصولات و پروژه‌های جدید تأمین مالی",
    ],
    members: [
      { name: "دکتر نوید رفیعی تبریزی", role: "رئیس کمیته" },
      { name: "محمدرضا صادقی مقدم", role: "عضو کمیته" },
      { name: "محمدسجاد سیاهکارزاده", role: "عضو کمیته" },
      { name: "اسماعیل گودرزی", role: "عضو کمیته" },
      { name: "محرم رزمجویی", role: "عضو کمیته" },
    ],
  },
  {
    icon: FiSettings,
    name: "کمیته سیاست‌گذاری",
    intro:
      "کمیته سیاست‌گذاری با هدف تدوین و هدایت سیاست‌های کلان شرکت و هم‌راستاسازی فعالیت‌ها با اهداف راهبردی تشکیل شده است.",
    duties: [
      "تدوین، بازنگری و پیشنهاد سیاست‌های کلان شرکت در حوزه‌های عملیاتی، مالی و سرمایه‌گذاری به هیئت‌مدیره",
      "تعیین چارچوب‌های راهبردی توسعه خدمات تأمین مالی و ابزارهای مالی",
      "بررسی و ارزیابی طرح‌ها و برنامه‌های کلان پیش از طرح در هیئت‌مدیره",
      "اطمینان از هم‌راستایی برنامه‌های اجرایی با اهداف استراتژیک شرکت",
      "پایش اجرای سیاست‌های مصوب و ارائه گزارش‌های دوره‌ای به هیئت‌مدیره",
    ],
    members: [
      { name: "محمد صفری", role: "رئیس کمیته" },
      { name: "محمد هاشمی‌نژاد", role: "عضو کمیته" },
      { name: "پیمان تاتائی", role: "عضو کمیته" },
      { name: "محمدرضا صادقی مقدم", role: "عضو کمیته" },
      { name: "محمد ابراهیم راعی عزآبادی", role: "عضو کمیته" },
      { name: "محمدرضا مایلی", role: "عضو کمیته" },
      { name: "محمد هادی بحرالعلوم", role: "عضو مستقل کمیته" },
    ],
  },
  {
    icon: FiUsers,
    name: "کمیته انتصابات",
    intro:
      "کمیته انتصابات با هدف ارتقای نظام حاکمیت شرکتی و اطمینان از شایستگی مدیران کلیدی شرکت فعالیت می‌کند.",
    duties: [
      "بررسی و ارزیابی صلاحیت حرفه‌ای مدیران کلیدی و ارشد",
      "پیشنهاد سیاست‌های مرتبط با جبران خدمات و نظام پاداش",
      "ارزیابی عملکرد مدیران ارشد و ارائه گزارش به هیئت‌مدیره",
    ],
    members: [
      { name: "سید محمدجواد فرهانیان", role: "رئیس کمیته" },
      { name: "مرتضی اثباتی", role: "عضو کمیته" },
      { name: "محمدحسین عسگری", role: "عضو کمیته" },
    ],
  },
];

export const SectionGovernance = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedMember, setSelectedMember] =
    useState<CommitteeMemberWithAvatar | null>(null);
  const headingColor = useColorModeValue("gray.900", "white");
  const textColor = useColorModeValue("gray.600", "gray.300");
  const cardBg = useColorModeValue("white", "dark.300");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const iconBg = useColorModeValue("brand.100", "icon.bg");
  const iconColor = useColorModeValue("brand.500", "icon.svg");
  const memberCardBg = useColorModeValue("gray.50", "whiteAlpha.60");
  const avatarBg = useColorModeValue("brand.50", "brand.200");
  const modalBg = useColorModeValue("white", "dark.200");

  const handleMemberClick = (member: CommitteeMember) => {
    setSelectedMember({
      ...member,
      avatar: committeeMemberAvatars[member.name] || "",
    });
    onOpen();
  };

  return (
    <Box>
      <Container maxW="container.xl">
        <VStack spacing={8} align="stretch">
          <VStack spacing={3} align="center" textAlign="center">
            <Heading
              as="h2"
              fontSize={{ base: "xl", md: "2xl", lg: "3xl" }}
              fontWeight="bold"
              color={headingColor}
            >
              حاکمیت شرکتی
            </Heading>
            <Text
              fontSize={{ base: "sm", md: "md" }}
              color={textColor}
              maxW="700px"
            >
              حاکمیت شرکتی در تأمین سرمایه امید، چارچوبی برای تصمیم‌گیری
              مسئولانه، نظارت مؤثر و پاسخ‌گویی شفاف در تمامی سطوح سازمان است.
            </Text>
          </VStack>

          <Accordion allowMultiple>
            {committees.map((c) => (
              <AccordionItem
                key={c.name}
                border="1px solid"
                borderColor={borderColor}
                borderRadius="xl"
                mb={3}
                overflow="hidden"
              >
                <AccordionButton py={4} px={5} bg={cardBg}>
                  <HStack flex={1} spacing={3}>
                    <Box bg={iconBg} p={2} borderRadius="lg" flexShrink={0}>
                      <Icon
                        verticalAlign="middle"
                        as={c.icon as any}
                        boxSize={6}
                        color={iconColor}
                      />
                    </Box>
                    <Text fontWeight="bold" fontSize="sm" color={headingColor}>
                      {c.name}
                    </Text>
                  </HStack>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={5} px={5}>
                  <VStack align="stretch" spacing={4}>
                    <Text fontSize="sm" color={textColor} lineHeight="1.8">
                      {c.intro}
                    </Text>

                    <Box>
                      <Text
                        fontWeight="bold"
                        fontSize="xs"
                        color={headingColor}
                        mb={2}
                      >
                        شرح وظایف:
                      </Text>
                      <List spacing={1}>
                        {c.duties.map((d, i) => (
                          <ListItem key={i} fontSize="xs" color={textColor}>
                            <ListIcon
                              as={FiCheck as any}
                              color="brand.500"
                              fontSize="xs"
                            />
                            {d}
                          </ListItem>
                        ))}
                      </List>
                    </Box>

                    <Box>
                      <Text
                        fontWeight="bold"
                        fontSize="xs"
                        color={headingColor}
                        mb={2}
                      >
                        اعضای کمیته:
                      </Text>
                      <SimpleGrid
                        columns={{ base: 2, md: 3, xl: 4 }}
                        spacing={3}
                      >
                        {c.members.map((m) => (
                          <VStack
                            key={m.name}
                            spacing={2}
                            align="center"
                            bg={memberCardBg}
                            border="1px solid"
                            borderColor={borderColor}
                            borderRadius="xl"
                            p={2.5}
                            textAlign="center"
                            cursor="pointer"
                            onClick={() => handleMemberClick(m)}
                            _hover={{
                              borderColor: "brand.200",
                              shadow: "sm",
                              transform: "translateY(-2px)",
                            }}
                            transition="all 0.2s"
                          >
                            {committeeMemberAvatars[m.name] ? (
                              <Image
                                src={committeeMemberAvatars[m.name]}
                                alt={m.name}
                                boxSize="56px"
                                borderRadius="lg"
                                objectFit="cover"
                              />
                            ) : (
                              <Box
                                boxSize="56px"
                                borderRadius="lg"
                                bg={avatarBg}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                              >
                                <Text
                                  fontSize="lg"
                                  fontWeight="bold"
                                  color="brand.500"
                                >
                                  {m.name.charAt(0)}
                                </Text>
                              </Box>
                            )}
                            <VStack align="center" spacing={0.5}>
                              <Text
                                fontSize="sm"
                                fontWeight="bold"
                                color={headingColor}
                                lineHeight="1.5"
                              >
                                {m.name}
                              </Text>
                              <Text fontSize="xs" color={textColor}>
                                {m.role}
                              </Text>
                            </VStack>
                          </VStack>
                        ))}
                      </SimpleGrid>
                    </Box>
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </VStack>
      </Container>

      <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent dir="rtl" borderRadius="xl" bg={modalBg}>
          <ModalCloseButton />
          <ModalHeader fontSize="md">{selectedMember?.name}</ModalHeader>
          <ModalBody pb={6}>
            {selectedMember?.avatar ? (
              <Image
                src={selectedMember.avatar}
                alt={selectedMember.name}
                w="100%"
                aspectRatio={1}
                objectFit="cover"
                borderRadius="xl"
                mb={4}
              />
            ) : null}
            <Text fontSize="sm" fontWeight="bold" color="brand.500" mb={2}>
              {selectedMember?.role}
            </Text>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};
